import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Avatar, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  FormControlLabel, Switch,
  Snackbar, Alert, Divider, Tabs, Tab, Grid, Card, CardContent, Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon, Edit as EditIcon,
  Refresh as RefreshIcon, Save as SaveIcon,
  PhoneAndroid as DeviceIcon, GpsFixed as GpsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { trackingApi, usersApi } from '../services/api';

const TC = ['#14548C','#f093fb','#4facfe','#43e97b','#fa709a','#00bcd4','#ff7043','#ab47bc','#26a69a','#ef5350'];
const roleColors: Record<string,string> = {SuperAdmin:'#f44336',Admin:'#ff9800',Dispatcher:'#2196f3',Technician:'#4caf50',Viewer:'#9e9e9e'};

interface UserData {
  id:number;username:string;email:string;first_name:string;last_name:string;full_name:string;
  phone:string|null;is_active:boolean;is_locked:boolean;job_title:string|null;department:string|null;
  roles:string[];device_id:string|null;tracking_enabled:boolean;
  work_start_address:string|null;work_start_city:string|null;work_start_state:string|null;
  work_start_lat:number|null;work_start_lng:number|null;
  home_address:string|null;home_city:string|null;home_state:string|null;
  work_phone:string|null;work_email:string|null;personal_phone:string|null;personal_email:string|null;
  employee_id:string|null;hire_date:string|null;last_login_at:string|null;created_at:string;
}
interface Device { device_id:string;device_name:string|null;platform:string|null;status:string;last_seen:string|null;assigned_to:any; }

export default function UsersPage() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [editUser, setEditUser] = useState<UserData|null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<any>({});
  const [snack, setSnack] = useState('');
  const [snackSev, setSnackSev] = useState<'success'|'error'|'info'>('info');
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [deviceUser, setDeviceUser] = useState<UserData|null>(null);

  const fetchUsers = useCallback(async()=>{
    setLoading(true);
    try { const r = await usersApi.list(); setUsers(r.data||[]); } catch(e){ console.error(e); }
    finally { setLoading(false); }
  },[]);

  const fetchDevices = useCallback(async()=>{
    try { const r = await trackingApi.getDevices(); setDevices(r.data?.devices||[]); } catch{}
  },[]);

  useEffect(()=>{ fetchUsers(); fetchDevices(); const iv=setInterval(()=>{fetchUsers();fetchDevices();},30000); return()=>clearInterval(iv); },[fetchUsers,fetchDevices]);

  const openEdit = (u:UserData) => { setEditUser(u); setForm({...u}); setEditOpen(true); };

  const saveUser = async () => {
    if(!editUser) return;
    try {
      await usersApi.update(editUser.id, form);
      setSnack('User updated'); setSnackSev('success'); setEditOpen(false); fetchUsers();
    } catch(e:any){ setSnack(e.response?.data?.detail||'Save failed'); setSnackSev('error'); }
  };

  const openDeviceDialog = (u:UserData) => { setDeviceUser(u); setDeviceDialogOpen(true); };

  const assignDevice = async (deviceId:string) => {
    if(!deviceUser) return;
    try {
      await trackingApi.assignDevice(deviceId, deviceUser.id);
      setSnack(`Device assigned to ${deviceUser.full_name}`); setSnackSev('success');
      setDeviceDialogOpen(false); fetchUsers(); fetchDevices();
    } catch(e:any){ setSnack('Assign failed'); setSnackSev('error'); }
  };

  const unassignDevice = async (u:UserData) => {
    if(!u.device_id) return;
    try {
      await trackingApi.unassignDevice(u.device_id);
      setSnack(`Device unassigned from ${u.full_name}`); setSnackSev('success'); fetchUsers(); fetchDevices();
    } catch{ setSnack('Unassign failed'); setSnackSev('error'); }
  };

  const techUsers = users.filter(u=>u.roles.includes('Technician'));
  const allUsers = users;

  return (
    <Box sx={{height:'100%',display:'flex',flexDirection:'column',overflow:'hidden'}}>
      <Paper elevation={0} sx={{px:2,py:1.5,borderBottom:'1px solid',borderColor:'divider',display:'flex',alignItems:'center',gap:1.5}}>
        <PeopleIcon color="primary"/><Typography variant="h6" fontWeight={700}>Team</Typography>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ml:2}}>
          <Tab label={`Technicians (${techUsers.length})`}/>
          <Tab label={`All Users (${allUsers.length})`}/>
        </Tabs>
        <Box sx={{flex:1}}/>
        <IconButton onClick={()=>{fetchUsers();fetchDevices();}} disabled={loading}>{loading?<CircularProgress size={18}/>:<RefreshIcon/>}</IconButton>
      </Paper>

      <Box sx={{flex:1,overflow:'auto',p:2}}>
        {tab===0 ? (
          <Grid container spacing={2}>
            {techUsers.map((u,i)=>(
              <Grid item xs={12} sm={6} md={4} lg={3} key={u.id}>
                <Card elevation={2} sx={{position:'relative'}}>
                  <CardContent>
                    <Box sx={{display:'flex',alignItems:'center',gap:1.5,mb:1.5}}>
                      <Avatar sx={{bgcolor:TC[i%TC.length],width:48,height:48,fontSize:18,fontWeight:700}}>
                        {u.first_name[0]}{u.last_name[0]}
                      </Avatar>
                      <Box sx={{flex:1,minWidth:0}}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>{u.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.job_title||'Field Technician'}</Typography>
                      </Box>
                      <IconButton size="small" onClick={()=>openEdit(u)}><EditIcon fontSize="small"/></IconButton>
                    </Box>
                    <Divider sx={{mb:1}}/>
                    <Typography variant="body2" color="text.secondary" sx={{mb:0.5}}>📧 {u.email}</Typography>
                    {u.phone&&<Typography variant="body2" color="text.secondary" sx={{mb:0.5}}>📱 {u.phone}</Typography>}
                    {(u.work_start_city||u.work_start_state)&&<Typography variant="body2" color="text.secondary" sx={{mb:0.5}}>📍 {[u.work_start_city,u.work_start_state].filter(Boolean).join(', ')}</Typography>}
                    <Divider sx={{my:1}}/>
                    <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                      <DeviceIcon sx={{fontSize:16,color:u.device_id?'success.main':'text.disabled'}}/>
                      {u.device_id ? (
                        <Box sx={{flex:1,minWidth:0}}>
                          <Typography variant="caption" fontWeight={600} color="success.main" noWrap>
                            {devices.find(d=>d.device_id===u.device_id)?.device_name||u.device_id.slice(0,20)}
                          </Typography>
                          <Chip label={u.tracking_enabled?'Tracking':'Paused'} size="small"
                            color={u.tracking_enabled?'success':'default'} sx={{ml:0.5,height:18,fontSize:10}}/>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">No device assigned</Typography>
                      )}
                      <Tooltip title={u.device_id?"Change device":"Assign device"}>
                        <IconButton size="small" onClick={()=>openDeviceDialog(u)}><GpsIcon fontSize="small"/></IconButton>
                      </Tooltip>
                      {u.device_id&&<Tooltip title="Unassign device"><IconButton size="small" color="error" onClick={()=>unassignDevice(u)}><CloseIcon fontSize="small"/></IconButton></Tooltip>}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Roles</TableCell>
                <TableCell>Status</TableCell><TableCell>Device</TableCell><TableCell>Last Login</TableCell><TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {allUsers.map(u=>(
                  <TableRow key={u.id} hover>
                    <TableCell><Box sx={{display:'flex',alignItems:'center',gap:1}}>
                      <Avatar sx={{width:28,height:28,fontSize:11}}>{u.first_name[0]}{u.last_name[0]}</Avatar>
                      <Box><Typography variant="body2" fontWeight={600}>{u.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.job_title||'—'}</Typography></Box>
                    </Box></TableCell>
                    <TableCell><Typography variant="body2">{u.email}</Typography></TableCell>
                    <TableCell>{u.roles.map(r=><Chip key={r} label={r} size="small" sx={{mr:0.5,height:20,fontSize:10,bgcolor:roleColors[r]||'#9e9e9e',color:'white'}}/>)}</TableCell>
                    <TableCell>{u.is_active?<Chip label="Active" size="small" color="success" sx={{height:20,fontSize:10}}/>:<Chip label="Inactive" size="small" color="error" sx={{height:20,fontSize:10}}/>}</TableCell>
                    <TableCell>{u.device_id?<Chip icon={<GpsIcon sx={{fontSize:12}}/>} label={devices.find(d=>d.device_id===u.device_id)?.device_name||'Assigned'} size="small" color="success" variant="outlined" sx={{height:22,fontSize:10}} onClick={()=>openDeviceDialog(u)}/>:<Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{u.last_login_at?new Date(u.last_login_at).toLocaleDateString():'Never'}</Typography></TableCell>
                    <TableCell><IconButton size="small" onClick={()=>openEdit(u)}><EditIcon fontSize="small"/></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={()=>setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit {editUser?.full_name}<IconButton size="small" onClick={()=>setEditOpen(false)} sx={{position:'absolute',right:8,top:8}}><CloseIcon/></IconButton></DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{mt:0}}>
            <Grid item xs={6}><TextField label="First Name" fullWidth size="small" value={form.first_name||''} onChange={e=>setForm({...form,first_name:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="Last Name" fullWidth size="small" value={form.last_name||''} onChange={e=>setForm({...form,last_name:e.target.value})}/></Grid>
            <Grid item xs={12}><TextField label="Email" fullWidth size="small" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="Phone" fullWidth size="small" value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="Employee ID" fullWidth size="small" value={form.employee_id||''} onChange={e=>setForm({...form,employee_id:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="Job Title" fullWidth size="small" value={form.job_title||''} onChange={e=>setForm({...form,job_title:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="Department" fullWidth size="small" value={form.department||''} onChange={e=>setForm({...form,department:e.target.value})}/></Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Work Start Location</Typography></Divider></Grid>
            <Grid item xs={12}><TextField label="Address" fullWidth size="small" value={form.work_start_address||''} onChange={e=>setForm({...form,work_start_address:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="City" fullWidth size="small" value={form.work_start_city||''} onChange={e=>setForm({...form,work_start_city:e.target.value})}/></Grid>
            <Grid item xs={6}><TextField label="State" fullWidth size="small" value={form.work_start_state||''} onChange={e=>setForm({...form,work_start_state:e.target.value})}/></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={form.is_active||false} onChange={e=>setForm({...form,is_active:e.target.checked})}/>} label="Active"/></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={form.tracking_enabled||false} onChange={e=>setForm({...form,tracking_enabled:e.target.checked})}/>} label="GPS Tracking Enabled"/></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={()=>setEditOpen(false)}>Cancel</Button><Button onClick={saveUser} variant="contained" startIcon={<SaveIcon/>}>Save</Button></DialogActions>
      </Dialog>

      {/* Device Assignment Dialog */}
      <Dialog open={deviceDialogOpen} onClose={()=>setDeviceDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Device to {deviceUser?.full_name}</DialogTitle>
        <DialogContent dividers>
          {devices.length===0?<Typography color="text.secondary">No tracker devices found</Typography>:(
            devices.map(d=>{
              const assigned = d.assigned_to;
              const isCurrent = deviceUser && assigned?.user_id===deviceUser.id;
              return (
                <Box key={d.device_id} sx={{display:'flex',alignItems:'center',gap:1.5,py:1,borderBottom:'1px solid',borderColor:'divider',
                  cursor:isCurrent?'default':'pointer','&:hover':{bgcolor:isCurrent?'transparent':'action.hover'},opacity:assigned&&!isCurrent?0.6:1}}
                  onClick={()=>!isCurrent&&assignDevice(d.device_id)}>
                  <DeviceIcon color={d.status==='active'?'success':'disabled'}/>
                  <Box sx={{flex:1}}>
                    <Typography variant="body2" fontWeight={600}>{d.device_name||d.device_id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.platform||'unknown'} • {d.last_seen?`Last seen ${new Date(d.last_seen).toLocaleString()}`:'Never seen'}
                    </Typography>
                    {assigned&&!isCurrent&&<Typography variant="caption" color="warning.main" display="block">Assigned to {assigned.user_name}</Typography>}
                  </Box>
                  {isCurrent&&<Chip label="Current" size="small" color="success"/>}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions><Button onClick={()=>setDeviceDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={()=>setSnack('')} anchorOrigin={{vertical:'bottom',horizontal:'center'}}>
        <Alert severity={snackSev} onClose={()=>setSnack('')} variant="filled">{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
